import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import { generateWhatsAppLink } from '../data/products';

const ProductCard = ({ product, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  // If hovered and secondary image exists, display secondary image
  const displayImage = isHovered && product.images.length > 1
    ? product.images[1]
    : product.images[currentImgIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="glass-panel card-hover-effect"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        background: '#ffffff',
        border: '1px solid rgba(15, 23, 42, 0.08)'
      }}
    >
      {/* Product Image Stage (4:5 Aspect Ratio) */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          paddingTop: '125%',
          overflow: 'hidden',
          backgroundColor: '#f1f5f9'
        }}
      >
        <motion.img
          key={displayImage}
          initial={{ opacity: 0.85 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          src={displayImage}
          alt={product.name}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center center',
            transition: 'transform 0.6s ease'
          }}
        />

        {/* Category Label Pill */}
        <div
          style={{
            position: 'absolute',
            top: '14px',
            left: '14px',
            background: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            padding: '4px 10px',
            borderRadius: '99px',
            fontSize: '10px',
            fontWeight: 900,
            color: 'var(--text-main)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)'
          }}
        >
          {product.categoryLabel}
        </div>

        {/* Secondary Image Indicator Dots */}
        {product.images.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              display: 'flex',
              gap: '5px',
              background: 'rgba(255, 255, 255, 0.85)',
              padding: '4px 8px',
              borderRadius: '99px',
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.1)'
            }}
          >
            {product.images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); setCurrentImgIndex(idx); }}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: idx === (isHovered && product.images.length > 1 ? 1 : currentImgIndex) ? 'var(--accent-magenta)' : 'rgba(15, 23, 42, 0.25)',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Product Content Details */}
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <h3
          style={{
            fontSize: '16px',
            fontFamily: 'var(--font-heading)',
            fontWeight: 900,
            color: 'var(--text-main)',
            marginBottom: '4px',
            lineHeight: 1.3
          }}
        >
          {product.name}
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px', flexGrow: 1 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-main)' }}>Color:</strong> {product.color}
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-main)' }}>Fit:</strong> {product.fit}
          </div>
        </div>

        {/* Export Enquiry Action Button */}
        <motion.a
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          href={generateWhatsAppLink(product.name)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: '11.5px', borderRadius: '8px' }}
        >
          <MessageSquare size={15} />
          <span>QUICK EXPORT ENQUIRY</span>
        </motion.a>
      </div>
    </motion.div>
  );
};

export default ProductCard;
